import React, { useCallback, useMemo, useRef, useState } from "react";
import { getNumericFontFamily } from "../../utils/fontUtils.js";
import { formatPrice } from "../../utils/priceUtils.js";
import {
  formatNumber,
  formatVolumeAbbrev,
} from "../../utils/numberFormatting.js";
import { DEFAULT_COLUMN_CONFIG } from "../../store/watchlistStore";
import "./WatchlistGrid.css";

const DASH = "–";
const PINNED_WIDTH = 3 + 13 + 4; // drag + symbol + actions
const AVAILABLE_WIDTH = 100 - PINNED_WIDTH;

// Cached Intl formatters for time/date cells
const _timeFmt = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});
const _dateFmt = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

// Cell renderers — pure functions, no React overhead
const cellRenderers = {
  direction: (row) => {
    const dir = row.direction;
    const arrow = dir === "up" ? "▲" : dir === "down" ? "▼" : "▲";
    const color =
      dir === "up" ? "#16a34a" : dir === "down" ? "#dc2626" : "transparent";
    return { text: arrow, style: { color } };
  },
  lastPrice: (row) => {
    const val = row.lastPrice;
    const flashClass =
      row.lastTick === "up"
        ? "wl-flash-up"
        : row.lastTick === "down"
          ? "wl-flash-down"
          : "";
    return {
      text: val != null && isFinite(val) ? formatPrice(row.symbol, val) : DASH,
      className: flashClass,
      key: row.tickId || 0,
    };
  },
  change: (row) => {
    const val = row.change;
    const color = val == null ? "inherit" : val >= 0 ? "#16a34a" : "#dc2626";
    return {
      text:
        val != null
          ? (val >= 0 ? "+" : "") + formatPrice(row.symbol, val)
          : DASH,
      style: { color },
    };
  },
  changePct: (row) => {
    const val = row.changePct;
    const color = val == null ? "inherit" : val >= 0 ? "#16a34a" : "#dc2626";
    return {
      text:
        val != null ? (val >= 0 ? "+" : "") + formatNumber(val, 2) + "%" : DASH,
      style: { color },
    };
  },
  high: (row) => {
    const val = row.high;
    return {
      text: val != null && isFinite(val) ? formatPrice(row.symbol, val) : DASH,
      className: row.highTickId ? "wl-flash-up" : "",
      key: row.highTickId || 0,
    };
  },
  low: (row) => {
    const val = row.low;
    return {
      text: val != null && isFinite(val) ? formatPrice(row.symbol, val) : DASH,
      className: row.lowTickId ? "wl-flash-down" : "",
      key: row.lowTickId || 0,
    };
  },
  volume: (row) => ({
    text:
      row.volume != null && isFinite(row.volume)
        ? formatVolumeAbbrev(row.volume, 2)
        : DASH,
  }),
  time: (row) => {
    const dt = row.time;
    if (!dt) return { text: DASH };
    const now = new Date();
    const isToday =
      dt.getDate() === now.getDate() &&
      dt.getMonth() === now.getMonth() &&
      dt.getFullYear() === now.getFullYear();
    return {
      text: isToday ? _timeFmt.format(dt) : _dateFmt.format(dt),
    };
  },
  open: (row) => ({
    text:
      row.open != null && isFinite(row.open)
        ? formatPrice(row.symbol, row.open)
        : DASH,
  }),
};

const WatchlistGrid = ({
  rows,
  onReorder,
  onRemove,
  onSymbolSelect,
  columnConfig,
}) => {
  const numFont = getNumericFontFamily();
  const config = columnConfig || DEFAULT_COLUMN_CONFIG;
  const dragRowRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Column config
  const { directionVisible, visibleColumns } = useMemo(() => {
    const dirCol = config.find((c) => c.key === "direction");
    const dirVisible = dirCol ? dirCol.visible : true;
    const vis = config.filter((c) => c.visible && c.key !== "direction");
    const totalBase = vis.reduce((sum, c) => sum + c.baseWidth, 0);
    return {
      directionVisible: dirVisible,
      visibleColumns: vis.map((c) => ({
        ...c,
        width: `${((c.baseWidth / totalBase) * AVAILABLE_WIDTH).toFixed(1)}%`,
        align: c.key === "direction" ? "center" : "right",
      })),
    };
  }, [config]);

  // --- Native HTML5 DnD for row reorder ---
  const handleDragStart = useCallback((e, idx) => {
    dragRowRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
    // Make the drag image semi-transparent
    e.currentTarget.style.opacity = "0.4";
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = "1";
    setDragOverIdx(null);
    dragRowRef.current = null;
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragRowRef.current !== idx) {
      setDragOverIdx(idx);
    }
  }, []);

  const handleDrop = useCallback(
    (e, dropIdx) => {
      e.preventDefault();
      const fromIdx = dragRowRef.current;
      if (fromIdx == null || fromIdx === dropIdx) return;
      const newOrder = rows.map((r) => r.symbol);
      const [moved] = newOrder.splice(fromIdx, 1);
      newOrder.splice(dropIdx, 0, moved);
      onReorder?.(newOrder);
      setDragOverIdx(null);
      dragRowRef.current = null;
    },
    [rows, onReorder],
  );

  if (!rows || rows.length === 0) {
    return <div className="wlg-empty">Nenhum símbolo</div>;
  }

  return (
    <div className="wlg-container">
      <table className="wlg-table">
        <thead>
          <tr>
            <th className="wlg-th wlg-drag-col" />
            {directionVisible && <th className="wlg-th wlg-dir-col" />}
            <th className="wlg-th wlg-sym-col">Símbolo</th>
            {visibleColumns.map((col) => (
              <th
                key={col.key}
                className="wlg-th wlg-data-col"
                style={{ textAlign: col.align }}
              >
                {col.label}
              </th>
            ))}
            <th className="wlg-th wlg-act-col" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.symbol}
              className={`wlg-row ${idx % 2 === 1 ? "wlg-row-stripe" : ""} ${dragOverIdx === idx ? "wlg-row-dragover" : ""}`}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
            >
              {/* Drag handle */}
              <td className="wlg-td wlg-drag-col">
                <span
                  className="wlg-drag-handle"
                  title="Arrastar para reordenar"
                >
                  ☰
                </span>
              </td>

              {/* Direction arrow */}
              {directionVisible && (
                <td className="wlg-td wlg-dir-col">
                  {(() => {
                    const r = cellRenderers.direction(row);
                    return (
                      <span style={{ ...r.style, fontSize: "11px" }}>
                        {r.text}
                      </span>
                    );
                  })()}
                </td>
              )}

              {/* Symbol */}
              <td className="wlg-td wlg-sym-col">
                <span
                  className="wlg-symbol"
                  onClick={() => onSymbolSelect?.(row.symbol)}
                  title="Clique para abrir no gráfico"
                >
                  {row.symbol}
                </span>
              </td>

              {/* Data columns */}
              {visibleColumns.map((col) => {
                const renderer = cellRenderers[col.key];
                if (!renderer)
                  return (
                    <td
                      key={col.key}
                      className="wlg-td wlg-data-col"
                      style={{ textAlign: col.align }}
                    >
                      {DASH}
                    </td>
                  );
                const r = renderer(row);
                return (
                  <td
                    key={col.key}
                    className="wlg-td wlg-data-col"
                    style={{ textAlign: col.align, fontFamily: numFont }}
                  >
                    <span
                      key={r.key}
                      className={r.className || ""}
                      style={r.style}
                    >
                      {r.text}
                    </span>
                  </td>
                );
              })}

              {/* Actions */}
              <td className="wlg-td wlg-act-col">
                <button
                  className="wlg-remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(row.symbol);
                  }}
                  title="Remover da lista"
                  data-evo-id="watchlistgrid-btn-remove-symbol"
                >
                  <i className="pi pi-trash" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(WatchlistGrid);
